import { Navigate, type RouteObject } from 'react-router-dom'
import { AppShell } from './AppShell'
import { RootLayout } from './RootLayout'
import {
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  Can,
  OrgRoleGate,
} from '@/features/auth'
import { LandingContentPage, LegalPagesPage, NewsAdminPage, CommercialsAdminPage, DifusionAdminPage } from '@/features/admin'
import { PaymentGatewaysAdminPage } from '@/features/admin/PaymentGatewaysAdminPage'
import { PaymentReturnPage } from '@/features/payments/pages/PaymentReturnPage'
import { LandingPage } from '@/features/marketing/LandingPage'
import { LegalPage } from '@/features/marketing/LegalPage'
import { NewsListPage } from '@/features/marketing/NewsListPage'
import { NewsDetailPage } from '@/features/marketing/NewsDetailPage'
import { DownloadsPage } from '@/features/marketing/DownloadsPage'
import { PublicLayout } from '@/features/marketing/PublicLayout'
import { ReleasesHistoryPage } from '@/features/releases'
import { NovedadesPage } from '@/features/novedades'
import { OrganizationPage, CreateOrganizationPage } from '@/features/organization'
import {
  BusinessesPage,
  BusinessDetailPage,
  CreateBusinessPage,
  FirstBusinessOnboarding,
} from '@/features/businesses'
import { UsersPage, TeamPage } from '@/features/users'
import { NotAuthorizedState } from '@/features/users/components/NotAuthorizedState'
import { ServicesPage } from '@/features/services'
import { CreditsPage } from '@/features/credits'
import { BillingPage, BillingDetailPage } from '@/features/billing'
import {
  TicketsPage,
  TicketDetailPage,
  CreateTicketPage,
  TicketScopeGate,
} from '@/features/tickets'
import { ComingSoonPage } from '@/shared/components/ui/coming-soon-page'

/**
 * Tabla de rutas, en fichero aparte del router a propósito: `index.tsx` solo
 * exporta el componente `AppRouter` (lo exige `react-refresh/only-export-
 * components`), y un test puede inspeccionar QUÉ rutas existen —que `/policy`
 * está y que `/privacidad` redirige— sin construir el `createBrowserRouter`,
 * que toca `window.history`, ni montar la aplicación entera.
 *
 * Las rutas van dentro de `routeConfig` bajo la capa `RootLayout` (ver abajo):
 * este `rutas` es solo el árbol, para no reindentar la tabla entera.
 */
const rutas: RouteObject[] = [
  // ---------------------------------------------------------------------------
  // ÁREA PÚBLICA — landing de marketing + autenticación propia (moqueada).
  // ---------------------------------------------------------------------------
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: <LandingPage /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/registro', element: <RegisterPage /> },
      { path: '/recuperar', element: <ResetPasswordPage /> },
      { path: '/terminos', element: <LegalPage doc="terms" /> },
      { path: '/policy', element: <LegalPage doc="privacy" /> },
      // La política vivía en `/privacidad`: hay enlaces pegados fuera del
      // portal (correos, tiendas, stores), así que la ruta vieja no se borra,
      // redirige. `replace` para no dejar basura en el historial.
      { path: '/privacidad', element: <Navigate to="/policy" replace /> },
      // La URL que se declara en Google Play para pedir el borrado de una
      // cuenta: página propia porque un ancla dentro de `/policy` no aterriza
      // (el texto llega del servidor después de que el navegador busque el ancla).
      { path: '/eliminar-cuenta', element: <LegalPage doc="accountDeletion" /> },
      // El conector de IA (MCP) y su privacidad: las URL que se declaran en el
      // registro MCP y en los directorios de Claude y de ChatGPT. Los revisores
      // de las tres tiendas las abren; no se renombran una vez enviadas.
      { path: '/conector-ia', element: <LegalPage doc="aiConnector" /> },
      { path: '/conector-ia/privacidad', element: <LegalPage doc="aiConnectorPrivacy" /> },
      { path: '/lo-ultimo', element: <NewsListPage /> },
      { path: '/lo-ultimo/:id', element: <NewsDetailPage /> },
      // Histórico de notas de versión (goal portal-notas-de-version-en-vivo):
      // paginado server-side, sin entrada en Header/Footer (decisión 5).
      { path: '/actualizaciones', element: <ReleasesHistoryPage /> },
      // Aplicaciones para descargar (goal el-portal-lista-las-aplicaciones-
      // para-descargar). La lista sale del CMS; `hidden.downloads` la apaga.
      { path: '/descargas', element: <DownloadsPage /> },
    ],
  },

  // Retorno de la pasarela externa tras pagar un link (pagos-zeus-3). Fuera del
  // PublicLayout: la pasarela redirige aquí y la página se basta sola.
  { path: '/pago/retorno', element: <PaymentReturnPage /> },

  // ---------------------------------------------------------------------------
  // ÁREA AUTENTICADA — el portal de control del cliente.
  // ---------------------------------------------------------------------------
  {
    path: '/app',
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/app/businesses" replace /> },
      // Gates de ruta de la matriz rol→superficie (goal portal-roles-1),
      // espejo del gate server-side del gateway. Tres estados: sin
      // organización → onboarding de primer negocio; con organización sin rol
      // → pantalla "pide un rol al dueño"; con rol → la página. Las rutas del
      // alta (organization/new, businesses/new) dejan pasar al usuario sin
      // organización para no romper el autoservicio.
      {
        path: 'organization',
        element: (
          <OrgRoleGate
            permission="organization:read"
            noOrgFallback={<FirstBusinessOnboarding />}
          >
            <OrganizationPage />
          </OrgRoleGate>
        ),
      },
      { path: 'organization/new', element: <CreateOrganizationPage /> },
      {
        path: 'businesses',
        element: (
          <OrgRoleGate
            permission="businesses:read"
            noOrgFallback={<FirstBusinessOnboarding />}
          >
            <BusinessesPage />
          </OrgRoleGate>
        ),
      },
      {
        path: 'businesses/new',
        element: (
          <OrgRoleGate permission="businesses:create" allowWithoutOrg>
            <CreateBusinessPage />
          </OrgRoleGate>
        ),
      },
      {
        path: 'businesses/:id',
        element: (
          <OrgRoleGate
            permission="businesses:read"
            noOrgFallback={<FirstBusinessOnboarding />}
          >
            <BusinessDetailPage />
          </OrgRoleGate>
        ),
      },
      { path: 'services', element: <ServicesPage /> },
      // Lo nuevo de cada producto (goal novedades-del-portal 2). SIN gate de
      // rol a propósito: las notas de versión ya son públicas en
      // `/actualizaciones` — cerrarlas por rol aquí sería teatro, y quien no
      // tiene rol es justo a quien más le sirve enterarse.
      { path: 'novedades', element: <NovedadesPage /> },
      {
        path: 'billing',
        element: (
          <OrgRoleGate
            permission="billing:read"
            noOrgFallback={<FirstBusinessOnboarding />}
          >
            <BillingPage />
          </OrgRoleGate>
        ),
      },
      {
        path: 'billing/:id',
        element: (
          <OrgRoleGate
            permission="billing:read"
            noOrgFallback={<FirstBusinessOnboarding />}
          >
            <BillingDetailPage />
          </OrgRoleGate>
        ),
      },
      {
        path: 'credits',
        element: (
          <OrgRoleGate
            permission="credits:read"
            noOrgFallback={<FirstBusinessOnboarding />}
          >
            <CreditsPage />
          </OrgRoleGate>
        ),
      },
      // Tickets de soporte por ALCANCE (goal portal-tickets-alcance-por-
      // membresia): la sección ya no es exclusiva del propietario — cualquier
      // usuario con organización o negocios entra. El gate de UI es la query
      // orgTicketScope (TicketScopeGate); el gate DURO es la resolución de
      // alcance contra Zeus + Identity en tickets-server.
      {
        path: 'tickets',
        element: (
          <TicketScopeGate>
            <TicketsPage />
          </TicketScopeGate>
        ),
      },
      {
        path: 'tickets/new',
        element: (
          <TicketScopeGate>
            <CreateTicketPage />
          </TicketScopeGate>
        ),
      },
      {
        path: 'tickets/:code',
        element: (
          <TicketScopeGate>
            <TicketDetailPage />
          </TicketScopeGate>
        ),
      },
      {
        path: 'statistics',
        element: (
          <ComingSoonPage
            title="Estadísticas"
            subtitle="Ventas por sucursal, ticket promedio, benchmarking del sector y más."
            tag="Fase 2"
          />
        ),
      },
      // Equipo de la organización (goal portal-roles-2): solo el dueño
      // (users:list es OWNER-only en el mapa). Un Gestor/Contable con rol ve
      // su 403 propio (NotAuthorizedState), no el "pide un rol al dueño".
      {
        path: 'equipo',
        element: (
          <OrgRoleGate
            permission="users:list"
            noOrgFallback={<FirstBusinessOnboarding />}
            noPermissionFallback={<NotAuthorizedState />}
          >
            <TeamPage />
          </OrgRoleGate>
        ),
      },
      {
        path: 'users',
        element: (
          <Can
            permission="admins:manage"
            fallback={
              <ComingSoonPage
                title="Acceso restringido"
                subtitle="Esta sección es exclusiva del administrador del portal."
                tag="403"
              />
            }
          >
            <UsersPage />
          </Can>
        ),
      },
      {
        path: 'admin/landing',
        element: (
          <Can
            permission="landing:edit"
            fallback={
              <ComingSoonPage
                title="Acceso restringido"
                subtitle="Esta sección es exclusiva del administrador del portal."
                tag="403"
              />
            }
          >
            <LandingContentPage />
          </Can>
        ),
      },
      {
        path: 'admin/legal',
        element: (
          <Can
            permission="landing:edit"
            fallback={
              <ComingSoonPage
                title="Acceso restringido"
                subtitle="Esta sección es exclusiva del administrador del portal."
                tag="403"
              />
            }
          >
            <LegalPagesPage />
          </Can>
        ),
      },
      {
        path: 'admin/news',
        element: (
          <Can
            permission="landing:edit"
            fallback={
              <ComingSoonPage
                title="Acceso restringido"
                subtitle="Esta sección es exclusiva del administrador del portal."
                tag="403"
              />
            }
          >
            <NewsAdminPage />
          </Can>
        ),
      },
      {
        path: 'admin/commercials',
        element: (
          <Can
            permission="landing:edit"
            fallback={
              <ComingSoonPage
                title="Acceso restringido"
                subtitle="Esta sección es exclusiva del administrador del portal."
                tag="403"
              />
            }
          >
            <CommercialsAdminPage />
          </Can>
        ),
      },
      {
        path: 'admin/difusion',
        element: (
          <Can
            permission="landing:edit"
            fallback={
              <ComingSoonPage
                title="Acceso restringido"
                subtitle="Esta sección es exclusiva del administrador del portal."
                tag="403"
              />
            }
          >
            <DifusionAdminPage />
          </Can>
        ),
      },
      {
        path: 'admin/payment-gateways',
        element: (
          <Can
            permission="payments:manage"
            fallback={
              <ComingSoonPage
                title="Acceso restringido"
                subtitle="Esta sección es exclusiva del administrador del portal."
                tag="403"
              />
            }
          >
            <PaymentGatewaysAdminPage />
          </Can>
        ),
      },
      {
        path: '*',
        element: (
          <ComingSoonPage
            title="Página no encontrada"
            subtitle="La ruta a la que intentas acceder no existe."
            tag="404"
          />
        ),
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // CATCH-ALL raíz — cualquier URL fuera de las rutas conocidas (p. ej.
  // `/prueba`) redirige al home en vez de mostrar el error boundary por
  // defecto de react-router ("Unexpected Application Error! 404").
  // ---------------------------------------------------------------------------
  { path: '*', element: <Navigate to="/" replace /> },
]

/**
 * Todo el árbol cuelga de una capa raíz SIN `path` (`RootLayout`), que se monta
 * una sola vez y reinicia el desplazamiento al cambiar de ruta — el arreglo es
 * de la navegación del portal, no de una página concreta. Una ruta sin `path`
 * no participa en el emparejado: las URLs son exactamente las mismas.
 */
export const routeConfig: RouteObject[] = [
  { element: <RootLayout />, children: rutas },
]
